import axios from "axios";
import { isProtectedPath } from "@/lib/paths";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth uses an httpOnly cookie set by the backend (XSS-hardened). The browser
// sends it automatically on same-origin requests; withCredentials ensures the
// cookie is included/accepted by axios.
export const http = axios.create({ baseURL: API, withCredentials: true });

http.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      // Only bounce to /login from authenticated areas (ERP /app or /portal).
      // Public routes (landing "/", "/login") must never force-redirect.
      if (isProtectedPath(window.location.pathname)) {
        window.location.href = "/login";
      }
    }
    // A billing-locked organization can still reach the billing page to settle up.
    if (err.response?.status === 403 && err.response?.data?.detail === "organization_billing_locked") {
      if (isProtectedPath(window.location.pathname) && !window.location.pathname.startsWith("/app/billing")) {
        window.location.href = "/app/billing";
      }
    }
    return Promise.reject(err);
  }
);

// ----- Auth -----
export const login = (email, password) =>
  http.post(`/auth/login`, { email, password }).then((r) => r.data);
export const signup = (payload) => http.post(`/auth/signup`, payload).then((r) => r.data);
export const verifyEmail = (token) => http.post(`/auth/verify-email`, { token }).then((r) => r.data);
export const resendVerification = (email) => http.post(`/auth/resend-verification`, { email }).then((r) => r.data);
export const activateAccount = (token, password) => http.post(`/auth/activate`, { token, password }).then((r) => r.data);
export const changePassword = (current_password, new_password) => http.post(`/auth/change-password`, { current_password, new_password }).then((r) => r.data);
export const forgotPassword = (email) => http.post(`/auth/forgot-password`, { email }).then((r) => r.data);
export const resetPassword = (token, password) => http.post(`/auth/reset-password`, { token, password }).then((r) => r.data);
export const resendInvite = (id) => http.post(`/auth/users/${id}/resend-invite`).then((r) => r.data);
export const adminSendReset = (id) => http.post(`/auth/users/${id}/send-reset`).then((r) => r.data);
export const getOrganizations = () => http.get(`/admin/organizations`).then((r) => r.data);
export const getPlatformUsage = (days = 30) => http.get(`/admin/usage`, { params: { days } }).then((r) => r.data);
export const runDormantTenantCheck = () => http.post(`/admin/usage/dormant-check`).then((r) => r.data);
export const getDormantAlerts = () => http.get(`/admin/usage/dormant-alerts`).then((r) => r.data);
export const getReengageDraft = (orgId) => http.get(`/admin/organizations/${orgId}/reengage/draft`, { params: { base: window.location.origin } }).then((r) => r.data);
export const sendReengageEmail = (orgId, payload) => http.post(`/admin/organizations/${orgId}/reengage`, payload).then((r) => r.data);
export const createOrganization = (payload) => http.post(`/admin/organizations`, payload).then((r) => r.data);
export const setOrgStatus = (id, status) => http.put(`/admin/organizations/${id}/status`, { status }).then((r) => r.data);
export const setOrgNotes = (id, notes) => http.put(`/admin/organizations/${id}/notes`, { notes }).then((r) => r.data);
export const deleteOrganization = (id) => http.delete(`/admin/organizations/${id}`).then((r) => r.data);

// ----- Billing / Invoicing (superadmin) -----
export const getBillingPlans = () => http.get(`/admin/plans`).then((r) => r.data);
export const getPublicPlans = () => http.get(`/public/plans`).then((r) => r.data);
export const createBillingPlan = (p) => http.post(`/admin/plans`, p).then((r) => r.data);
export const updateBillingPlan = (id, p) => http.put(`/admin/plans/${id}`, p).then((r) => r.data);
export const deleteBillingPlan = (id) => http.delete(`/admin/plans/${id}`).then((r) => r.data);
export const setOrgPlan = (orgId, planId) => http.put(`/admin/organizations/${orgId}/plan`, { plan_id: planId }).then((r) => r.data);
export const getAdminInvoices = (params = {}) => http.get(`/admin/invoices`, { params }).then((r) => r.data);
export const createInvoice = (p) => http.post(`/admin/invoices`, p).then((r) => r.data);
export const updateInvoice = (id, p) => http.put(`/admin/invoices/${id}`, p).then((r) => r.data);
export const setInvoiceStatus = (id, status) => http.put(`/admin/invoices/${id}/status`, { status }).then((r) => r.data);
export const deleteInvoice = (id) => http.delete(`/admin/invoices/${id}`).then((r) => r.data);

// ----- Billing / Invoicing (org admin) -----
export const getMyBilling = () => http.get(`/billing/plan`).then((r) => r.data);
export const getMyInvoices = () => http.get(`/billing/invoices`).then((r) => r.data);

// Open an invoice PDF in a new tab (cookie-authenticated blob fetch).
export const openInvoicePdf = async (path) => {
  const res = await http.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
export const logout = () => http.post(`/auth/logout`).then((r) => r.data);
export const getMe = () => http.get(`/auth/me`).then((r) => r.data);
export const bookDemo = (payload) => http.post(`/book-demo`, payload).then((r) => r.data);
export const getMyPermissions = () => http.get(`/me/permissions`).then((r) => r.data);
export const getRolePermissions = () => http.get(`/admin/role-permissions`).then((r) => r.data);
export const updateRolePermissions = (role, sections) =>
  http.put(`/admin/role-permissions`, { role, sections }).then((r) => r.data);
export const getUsers = () => http.get(`/auth/users`).then((r) => r.data);
export const createUser = (payload) => http.post(`/auth/users`, payload).then((r) => r.data);
export const updateUser = (id, payload) => http.put(`/auth/users/${id}`, payload).then((r) => r.data);
export const deleteUser = (id) => http.delete(`/auth/users/${id}`).then((r) => r.data);

// CSV download with auth header
export const downloadHaccpCsv = async (params = {}) => {
  const res = await http.get(`/haccp/export`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `haccp_logs_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Generic authed PDF download
export const downloadPdf = async (path, filename) => {
  const res = await http.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Authed PDF download via POST (for endpoints that take a request body)
export const downloadPdfPost = async (path, body, filename) => {
  const res = await http.post(path, body, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ----- Standardization plans -----
export const getStdPlans = () => http.get(`/standardization-plans`).then((r) => r.data);
export const createStdPlan = (payload) => http.post(`/standardization-plans`, payload).then((r) => r.data);
export const deleteStdPlan = (id) => http.delete(`/standardization-plans/${id}`).then((r) => r.data);

// ----- Production plans -----
export const getPlans = (params = {}) => http.get(`/plans`, { params }).then((r) => r.data);
export const createPlan = (payload) => http.post(`/plans`, payload).then((r) => r.data);
export const createPlansBulk = (items, source = "planner") => http.post(`/plans/bulk`, { items, source }).then((r) => r.data);
export const updatePlan = (id, payload) => http.put(`/plans/${id}`, payload).then((r) => r.data);
export const deletePlan = (id) => http.delete(`/plans/${id}`).then((r) => r.data);

// ----- Milk reception plans -----
export const getReceptionPlans = (params = {}) => http.get(`/reception-plans`, { params }).then((r) => r.data);
export const createReceptionPlan = (payload) => http.post(`/reception-plans`, payload).then((r) => r.data);
export const updateReceptionPlan = (id, payload) => http.put(`/reception-plans/${id}`, payload).then((r) => r.data);
export const deleteReceptionPlan = (id) => http.delete(`/reception-plans/${id}`).then((r) => r.data);

// ----- Calendar & analytics -----
export const getCalendar = (start, end) =>
  http.get(`/calendar`, { params: { start, end } }).then((r) => r.data);
export const getTrends = (period = "current_month") =>
  http.get(`/analytics/trends`, { params: { period } }).then((r) => r.data);
export const getSalesTrend = (period = "30d", product = "") =>
  http.get(`/dashboard/sales-trend`, { params: { period, ...(product ? { product } : {}) } }).then((r) => r.data);
export const getStockMovements = (params = {}) =>
  http.get(`/reports/stock-movements`, { params }).then((r) => r.data);
export const downloadStockMovementsXlsx = async (params = {}) => {
  const res = await http.get(`/reports/stock-movements.xlsx`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `stock_movements_${params.start || "all"}_${params.end || "all"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadProductionsXlsx = async (params = {}) => {
  const res = await http.get(`/reports/productions.xlsx`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `productions_report_${params.date || params.month || "all"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const getConsumptionTrend = (params) => http.get(`/reports/consumption-trend`, { params }).then((r) => r.data);
export const getLossTrend = (params) => http.get(`/reports/loss-trend`, { params }).then((r) => r.data);
export const sendWeeklyDigestTest = () => http.post(`/reports/weekly-digest/test`).then((r) => r.data);
export const updateAlertPreferences = (payload) => http.put(`/auth/me/alert-preferences`, payload).then((r) => r.data);
export const sendDailyAuditSummaryTest = () => http.post(`/reports/daily-audit-summary/test`).then((r) => r.data);
export const getNotifications = () => http.get(`/notifications`).then((r) => r.data);
export const dismissNotification = (id, state_key = "") => http.post(`/notifications/dismiss`, { id, state_key }).then((r) => r.data);
export const dismissAllNotifications = (items) => http.post(`/notifications/dismiss-all`, { items }).then((r) => r.data);
export const undoDismissNotification = (id) => http.delete(`/notifications/dismiss/${encodeURIComponent(id)}`).then((r) => r.data);
export const restoreAllNotifications = () => http.delete(`/notifications/dismissals`).then((r) => r.data);
export const getActivityLogs = (params = {}) => http.get(`/activity-logs`, { params }).then((r) => r.data);
export const exportActivityLogsXlsx = async (params = {}) => {
  const res = await http.get(`/activity-logs.xlsx`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `activity-log_${params.start || "all"}_${params.end || "all"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const getSupplierTrend = (months = 6) => http.get(`/milk-batches/supplier-trend`, { params: { months } }).then((r) => r.data);



// ----- Milk batches -----
export const getMilkBatches = (onlyAvailable = false) =>
  http.get(`/milk-batches`, { params: { only_available: onlyAvailable } }).then((r) => r.data);
export const getMilkBatch = (id) => http.get(`/milk-batches/${id}`).then((r) => r.data);
export const createMilkBatch = (payload) => http.post(`/milk-batches`, payload).then((r) => r.data);
export const updateMilkBatch = (id, payload) => http.put(`/milk-batches/${id}`, payload).then((r) => r.data);
export const deleteMilkBatch = (id) => http.delete(`/milk-batches/${id}`).then((r) => r.data);

// ----- Suppliers -----
export const getSuppliers = (params = {}) => http.get(`/suppliers`, { params }).then((r) => r.data);
export const createSupplier = (p) => http.post(`/suppliers`, p).then((r) => r.data);
export const updateSupplier = (id, p) => http.put(`/suppliers/${id}`, p).then((r) => r.data);
export const deleteSupplier = (id) => http.delete(`/suppliers/${id}`).then((r) => r.data);

// ----- Pearson -----
export const calcPearson = (payload) => http.post(`/pearson`, payload).then((r) => r.data);

// ----- Templates -----
export const getTemplates = (productType) =>
  http.get(`/templates`, { params: productType ? { product_type: productType } : {} }).then((r) => r.data);
export const getTemplate = (id) => http.get(`/templates/${id}`).then((r) => r.data);
export const createTemplate = (payload) => http.post(`/templates`, payload).then((r) => r.data);
export const updateTemplate = (id, payload) => http.put(`/templates/${id}`, payload).then((r) => r.data);
export const deleteTemplate = (id) => http.delete(`/templates/${id}`).then((r) => r.data);

// ----- Productions -----
export const getProductions = (params = {}) => http.get(`/productions`, { params }).then((r) => r.data);
export const getProduction = (id) => http.get(`/productions/${id}`).then((r) => r.data);
export const createProduction = (payload) => http.post(`/productions`, payload).then((r) => r.data);
export const updateProduction = (id, payload) => http.put(`/productions/${id}`, payload).then((r) => r.data);
export const deleteProduction = (id) => http.delete(`/productions/${id}`).then((r) => r.data);
export const addVariantAllocation = (productionId, payload) =>
  http.post(`/productions/${productionId}/variant-allocations`, payload).then((r) => r.data);
export const deleteVariantAllocation = (productionId, allocId) =>
  http.delete(`/productions/${productionId}/variant-allocations/${allocId}`).then((r) => r.data);

// ----- Production statuses (workflow) -----
export const getProductionStatuses = () => http.get(`/production-statuses`).then((r) => r.data);
export const createProductionStatus = (payload) => http.post(`/production-statuses`, payload).then((r) => r.data);
export const updateProductionStatus = (id, payload) => http.put(`/production-statuses/${id}`, payload).then((r) => r.data);
export const deleteProductionStatus = (id) => http.delete(`/production-statuses/${id}`).then((r) => r.data);
export const reorderProductionStatuses = (ids) => http.put(`/production-statuses/reorder`, { ids }).then((r) => r.data);

// ----- Product settings (shelf life per product type) -----
// ----- Product settings (shelf life + maturation per product type) -----
export const getProductSettings = () => http.get(`/product-settings`).then((r) => r.data);
export const setProductSetting = (productType, patch) =>
  http.put(`/product-settings/${encodeURIComponent(productType)}`, patch).then((r) => r.data);

// ----- Product types (user-manageable list) -----
export const getProductTypes = () => http.get(`/product-types`).then((r) => r.data);
export const createProductType = (p) => http.post(`/product-types`, p).then((r) => r.data);
export const updateProductType = (id, p) => http.put(`/product-types/${id}`, p).then((r) => r.data);
export const deleteProductType = (id) => http.delete(`/product-types/${id}`).then((r) => r.data);
export const mergeProductType = (id, target) => http.post(`/product-types/${id}/merge`, { target }).then((r) => r.data);

// ----- Dashboard -----
export const getDashboard = () => http.get(`/dashboard`).then((r) => r.data);
export const getProductionPlanner = (params = {}) => http.get(`/production-planner`, { params }).then((r) => r.data);
export const getMilkSummary = (params = {}) => http.get(`/dashboard/milk-summary`, { params }).then((r) => r.data);

// ----- HACCP -----
export const getCCPs = () => http.get(`/haccp/ccps`).then((r) => r.data);
export const createCCP = (payload) => http.post(`/haccp/ccps`, payload).then((r) => r.data);
export const updateCCP = (id, payload) => http.put(`/haccp/ccps/${id}`, payload).then((r) => r.data);
export const deleteCCP = (id) => http.delete(`/haccp/ccps/${id}`).then((r) => r.data);
export const getHaccpLogs = (params = {}) => http.get(`/haccp/logs`, { params }).then((r) => r.data);
export const createHaccpLog = (payload) => http.post(`/haccp/logs`, payload).then((r) => r.data);
export const updateHaccpLog = (logId, payload) => http.put(`/haccp/logs/${logId}`, payload).then((r) => r.data);
export const resolveHaccpLog = (logId) => http.post(`/haccp/logs/${logId}/resolve`).then((r) => r.data);
export const addCorrectiveAction = (logId, payload) =>
  http.post(`/haccp/logs/${logId}/corrective-action`, payload).then((r) => r.data);
export const deleteHaccpLog = (logId) => http.delete(`/haccp/logs/${logId}`).then((r) => r.data);

// ----- File uploads (photo evidence) -----
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post(`/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
// Authenticated blob URL for displaying a stored image in <img src>
export const fetchFileBlobUrl = async (storagePath) => {
  const res = await http.get(`/files/${storagePath}`, { responseType: "blob" });
  return window.URL.createObjectURL(res.data);
};

// Default/fallback product types (used until the dynamic list loads).
// The authoritative, user-manageable list comes from GET /product-types.
export const PRODUCT_TYPES = [
  "Telemea",
  "Mozzarella",
  "Cream Cheese",
  "Yogurt",
  "Kefir",
  "Ayran",
  "Fresh Cottage Cheese",
  "Diet Fresh Cottage Cheese",
];
export const DEFAULT_PRODUCT_TYPES = PRODUCT_TYPES;

export const fmt = (n, d = 1) => {
  if (n === null || n === undefined || n === "") return "-";
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toLocaleString(undefined, { maximumFractionDigits: d });
};

export const SELL_UNITS = ["kg", "pcs"];
export const PRICE_LEVELS = [1, 2, 3, 4, 5];

// ----- Sales: Clients -----
export const getClients = () => http.get(`/clients`).then((r) => r.data);
export const createClient = (p) => http.post(`/clients`, p).then((r) => r.data);
export const updateClient = (id, p) => http.put(`/clients/${id}`, p).then((r) => r.data);
export const deleteClient = (id) => http.delete(`/clients/${id}`).then((r) => r.data);
export const setClientPortal = (id, p) => http.post(`/clients/${id}/portal-access`, p).then((r) => r.data);

// ----- Sales: Stock lots -----
export const getStockLots = (params = {}) => http.get(`/stock-lots`, { params }).then((r) => r.data);export const createStockLot = (p) => http.post(`/stock-lots`, p).then((r) => r.data);
export const updateStockLot = (id, p) => http.put(`/stock-lots/${id}`, p).then((r) => r.data);
export const setStockLotRemaining = (id, p) => http.put(`/stock-lots/${id}/remaining`, p).then((r) => r.data);
export const getLossReasons = () => http.get(`/loss-reasons`).then((r) => r.data);
export const addLossReason = (label) => http.post(`/loss-reasons`, { label }).then((r) => r.data);
export const deleteLossReason = (label) => http.delete(`/loss-reasons`, { params: { label } }).then((r) => r.data);
export const getLosses = (params = {}) => http.get(`/losses`, { params }).then((r) => r.data);
export const revertLoss = (id) => http.post(`/losses/${id}/revert`).then((r) => r.data);
export const getNonconformities = (params = {}) => http.get(`/reports/nonconformities`, { params }).then((r) => r.data);
export const updateNonconformity = (productionId, payload) => http.put(`/nonconformities/${productionId}`, payload).then((r) => r.data);
export const downloadNonconformitiesXlsx = async (params = {}) => {
  const res = await http.get(`/reports/nonconformities.xlsx`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `nonconformities_${params.start || "all"}_${params.end || "all"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const downloadLossesXlsx = async (params = {}) => {
  const res = await http.get(`/losses.xlsx`, { params, responseType: "blob" });
  const url = window.URL.createObjectURL(
    new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `losses_${params.start || "all"}_${params.end || "all"}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
export const rebatchStockLot = (p) => http.post(`/stock-lots/rebatch`, p).then((r) => r.data);
export const takeLotOutOfFreezer = (id) => http.post(`/stock-lots/${id}/take-out-of-freezer`).then((r) => r.data);
export const deleteStockLot = (id) => http.delete(`/stock-lots/${id}`).then((r) => r.data);
export const getStockAvailability = (date) => http.get(`/stock-availability`, { params: date ? { date } : {} }).then((r) => r.data);
export const getYieldSimulatorProducts = () => http.get(`/yield-simulator/products`).then((r) => r.data);

// ----- Sales: Price lists -----
export const getPriceLists = () => http.get(`/price-lists`).then((r) => r.data);
export const upsertPriceList = (p) => http.put(`/price-lists`, p).then((r) => r.data);
export const deletePriceList = (id) => http.delete(`/price-lists/${id}`).then((r) => r.data);

// ----- Sales: Orders -----
export const getOrders = (params = {}) => http.get(`/orders`, { params }).then((r) => r.data);
export const getOrder = (id) => http.get(`/orders/${id}`).then((r) => r.data);
export const createOrder = (p) => http.post(`/orders`, p).then((r) => r.data);
export const updateOrder = (id, p) => http.put(`/orders/${id}`, p).then((r) => r.data);
export const updateOrderStatus = (id, status, extra = {}) => http.put(`/orders/${id}/status`, { status, ...extra }).then((r) => r.data);
export const deleteOrder = (id) => http.delete(`/orders/${id}`).then((r) => r.data);

// ----- Sales: Delivery notes -----
export const getDeliveryNotes = (params = {}) => http.get(`/delivery-notes`, { params }).then((r) => r.data);
export const getDeliveryNote = (id) => http.get(`/delivery-notes/${id}`).then((r) => r.data);
export const createDeliveryNote = (p) => http.post(`/delivery-notes`, p).then((r) => r.data);
export const deleteDeliveryNote = (id) => http.delete(`/delivery-notes/${id}`).then((r) => r.data);
export const updateDeliveryNoteRefs = (id, body) =>
  http.patch(`/delivery-notes/${id}/refs`, body).then((r) => r.data);
export const bulkManualDeliveryNotes = (payload) => http.post(`/delivery-notes/bulk-manual`, payload).then((r) => r.data);
export const bulkDeliveryNotes = (orderIds, extra = {}) =>
  http.post(`/delivery-notes/bulk`, { order_ids: orderIds, ...extra }).then((r) => r.data);
export const getFulfillmentSources = (productType, unit) =>
  http.get(`/fulfillment-sources`, { params: { product_type: productType, unit } }).then((r) => r.data);
export const downloadCertificate = (id, code, lang) =>
  downloadPdf(`/delivery-notes/${id}/certificate${lang ? `?lang=${lang}` : ""}`, `certificate_${code || id}.pdf`);

export const downloadPackingList = (id, code, lang) =>
  downloadPdf(`/delivery-notes/${id}/packing-list${lang ? `?lang=${lang}` : ""}`, `packing_list_${code || id}.pdf`);

// Combined PDF (one document per page) for multiple delivery notes
export const downloadCertificatesBulk = (ids, lang) =>
  downloadPdfPost(`/delivery-notes/certificates`, { ids, lang }, `certificates_${new Date().toISOString().slice(0, 10)}.pdf`);

export const downloadPackingListsBulk = (ids, lang) =>
  downloadPdfPost(`/delivery-notes/packing-lists`, { ids, lang }, `packing_lists_${new Date().toISOString().slice(0, 10)}.pdf`);

// ----- Packaging options -----
export const getPackagingOptions = () => http.get(`/packaging-options`).then((r) => r.data);
export const createPackagingOption = (p) => http.post(`/packaging-options`, p).then((r) => r.data);
export const updatePackagingOption = (id, p) => http.put(`/packaging-options/${id}`, p).then((r) => r.data);
export const deletePackagingOption = (id) => http.delete(`/packaging-options/${id}`).then((r) => r.data);

// ----- Raw materials (rennet, cultures, salt, additives) -----
export const getRawMaterialTypes = (includeInactive = false) =>
  http.get(`/raw-material-types`, { params: { include_inactive: includeInactive } }).then((r) => r.data);
export const createRawMaterialType = (p) => http.post(`/raw-material-types`, p).then((r) => r.data);
export const updateRawMaterialType = (id, p) => http.put(`/raw-material-types/${id}`, p).then((r) => r.data);
export const deleteRawMaterialType = (id) => http.delete(`/raw-material-types/${id}`).then((r) => r.data);

export const getRawMaterialLots = (params = {}) =>
  http.get(`/raw-material-lots`, { params }).then((r) => r.data);
export const createRawMaterialLot = (p) => http.post(`/raw-material-lots`, p).then((r) => r.data);
export const updateRawMaterialLot = (id, p) => http.put(`/raw-material-lots/${id}`, p).then((r) => r.data);
export const deleteRawMaterialLot = (id) => http.delete(`/raw-material-lots/${id}`).then((r) => r.data);

// Raw material config (editable categories & units). kind = "categories" | "units"
export const getRmConfig = () => http.get(`/rm-config`).then((r) => r.data);
export const addRmConfigItem = (kind, value) => http.post(`/rm-config/${kind}`, { value }).then((r) => r.data);
export const renameRmConfigItem = (kind, oldValue, newValue) =>
  http.put(`/rm-config/${kind}`, { old: oldValue, new: newValue }).then((r) => r.data);
export const deleteRmConfigItem = (kind, value) =>
  http.delete(`/rm-config/${kind}`, { params: { value } }).then((r) => r.data);

// ----- Expenses (operating costs) -----
export const getExpenses = (params = {}) => http.get(`/expenses`, { params }).then((r) => r.data);
export const createExpense = (p) => http.post(`/expenses`, p).then((r) => r.data);
export const updateExpense = (id, p) => http.put(`/expenses/${id}`, p).then((r) => r.data);
export const deleteExpense = (id) => http.delete(`/expenses/${id}`).then((r) => r.data);
export const getExpenseConfig = () => http.get(`/expense-config`).then((r) => r.data);
export const addExpenseCategory = (value) => http.post(`/expense-config/categories`, { value }).then((r) => r.data);
export const renameExpenseCategory = (oldValue, newValue) =>
  http.put(`/expense-config/categories`, { old: oldValue, new: newValue }).then((r) => r.data);
export const deleteExpenseCategory = (value) =>
  http.delete(`/expense-config/categories`, { params: { value } }).then((r) => r.data);

// ----- Recurring expenses -----
export const getRecurringExpenses = () => http.get(`/recurring-expenses`).then((r) => r.data);
export const createRecurringExpense = (p) => http.post(`/recurring-expenses`, p).then((r) => r.data);
export const updateRecurringExpense = (id, p) => http.put(`/recurring-expenses/${id}`, p).then((r) => r.data);
export const deleteRecurringExpense = (id, deleteGenerated = false) =>
  http.delete(`/recurring-expenses/${id}`, { params: { delete_generated: deleteGenerated } }).then((r) => r.data);


// ----- Product packaging (fixed packaging list + stock mode per product) -----
export const getProductPackaging = () => http.get(`/product-packaging`).then((r) => r.data);
export const setProductPackaging = (product, units, stockMode, eans, codes) =>
  http.put(`/product-packaging`, {
    product, units,
    ...(stockMode ? { stock_mode: stockMode } : {}),
    ...(eans ? { eans } : {}),
    ...(codes ? { codes } : {}),
  }).then((r) => r.data);

// Build a {product: {label: ean}} map from the GET /product-packaging list response.
export const eanMapFromList = (list) => {
  const m = {};
  (list || []).forEach((d) => { if (d?.product && d.eans && typeof d.eans === "object") m[d.product] = d.eans; });
  return m;
};

// Build a {product: {label: code}} map from the GET /product-packaging list response.
export const codeMapFromList = (list) => {
  const m = {};
  (list || []).forEach((d) => { if (d?.product && d.codes && typeof d.codes === "object") m[d.product] = d.codes; });
  return m;
};

// Build a {product: [labels]} map from the GET /product-packaging list response.
export const packagingMapFromList = (list) => {
  const m = {};
  (list || []).forEach((d) => { if (d?.product && (d.units || []).length) m[d.product] = d.units; });
  return m;
};

// Build a {product: "bulk_kg"|"package"} map of explicit stock modes.
export const stockModeMapFromList = (list) => {
  const m = {};
  (list || []).forEach((d) => { if (d?.product && (d.stock_mode === "bulk_kg" || d.stock_mode === "package")) m[d.product] = d.stock_mode; });
  return m;
};

// Filter a global packaging-options list to those allowed for a product.
// The bulk "kg" master unit is always allowed; products with no config allow all.
export const allowedPackagings = (pkgMap, product, globalList) => {
  const cfg = pkgMap?.[product];
  if (!cfg || cfg.length === 0) return globalList;
  return (globalList || []).filter((u) => u.label === "kg" || cfg.includes(u.label));
};

// ----- Company settings -----
export const getCompanySettings = () => http.get(`/company-settings`).then((r) => r.data);
export const getPlatformBackups = () => http.get(`/admin/platform-backups`).then((r) => r.data);
export const runPlatformBackup = () => http.post(`/admin/platform-backups/run`).then((r) => r.data);
export const getPlatformBackupStatus = () => http.get(`/admin/platform-backups/status`).then((r) => r.data);
export const restorePlatformBackup = (id, confirm) =>
  http.post(`/admin/platform-backups/${id}/restore`, { confirm }).then((r) => r.data);
export const restoreTenantBackup = (id, orgId, confirm) =>
  http.post(`/admin/platform-backups/${id}/restore-tenant`, { org_id: orgId, confirm }).then((r) => r.data);
export const downloadPlatformBackup = async (id) => {
  const res = await http.get(`/admin/platform-backups/${id}/download`, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m ? m[1] : `platform-backup-${id}.zip`;
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
};
export const updateCompanySettings = (p) => http.put(`/company-settings`, p).then((r) => r.data);
export const downloadTenantBackup = async (format) => {
  const res = await http.get(`/backup/export.${format}`, { responseType: "blob" });
  const cd = res.headers["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const filename = m ? m[1] : `lactopilot-backup.${format}`;
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ----- Shop (client portal) -----
export const getShopProfile = () => http.get(`/shop/profile`).then((r) => r.data);
export const getShopCatalog = () => http.get(`/shop/catalog`).then((r) => r.data);
export const shopPlaceOrder = (p) => http.post(`/shop/orders`, p).then((r) => r.data);
export const getShopOrders = () => http.get(`/shop/orders`).then((r) => r.data);

// ----- Staff: Operators -----
export const getOperators = (params = {}) => http.get(`/operators`, { params }).then((r) => r.data);
export const getStaffUsers = () => http.get(`/staff-users`).then((r) => r.data);
export const createOperator = (p) => http.post(`/operators`, p).then((r) => r.data);
export const updateOperator = (id, p) => http.put(`/operators/${id}`, p).then((r) => r.data);
export const deleteOperator = (id) => http.delete(`/operators/${id}`).then((r) => r.data);

// ----- Staff: Time clock -----
export const getTimeClockStatus = () => http.get(`/time-clock/status`).then((r) => r.data);
export const getTimeEntries = (params = {}) => http.get(`/time-clock/entries`, { params }).then((r) => r.data);
export const clockIn = (operatorId) => http.post(`/time-clock/clock-in`, { operator_id: operatorId }).then((r) => r.data);
export const clockOut = (operatorId) => http.post(`/time-clock/clock-out`, { operator_id: operatorId }).then((r) => r.data);
export const getMyClock = () => http.get(`/time-clock/me`).then((r) => r.data);
export const clockInMe = () => http.post(`/time-clock/me/clock-in`).then((r) => r.data);
export const clockOutMe = () => http.post(`/time-clock/me/clock-out`).then((r) => r.data);

// Admin/manager manual time-entry management
export const createTimeEntry = (payload) => http.post(`/time-clock/entries`, payload).then((r) => r.data);
export const updateTimeEntry = (id, payload) => http.put(`/time-clock/entries/${id}`, payload).then((r) => r.data);
export const deleteTimeEntry = (id) => http.delete(`/time-clock/entries/${id}`).then((r) => r.data);
export const getTimeClockSummary = (params = {}) => http.get(`/time-clock/summary`, { params }).then((r) => r.data);
export const getLeaves = (params = {}) => http.get(`/time-clock/leaves`, { params }).then((r) => r.data);
export const createLeave = (payload) => http.post(`/time-clock/leaves`, payload).then((r) => r.data);
export const deleteLeave = (id) => http.delete(`/time-clock/leaves/${id}`).then((r) => r.data);
export const getHolidays = (params = {}) => http.get(`/time-clock/holidays`, { params }).then((r) => r.data);
export const createHoliday = (payload) => http.post(`/time-clock/holidays`, payload).then((r) => r.data);
export const deleteHoliday = (id) => http.delete(`/time-clock/holidays/${id}`).then((r) => r.data);

// ----- Subproduct (by-product) inventory lots -----
export const getSubproductLots = ({ names, availableOnly = true } = {}) =>
  http.get(`/subproduct-lots`, {
    params: {
      available_only: availableOnly,
      ...(names && names.length ? { name: names.join(",") } : {}),
    },
  }).then((r) => r.data);
export const setSubproductLotRemaining = (id, payload) =>
  http.put(`/subproduct-lots/${id}/remaining`, payload).then((r) => r.data);

export const getKioskOperators = () => http.get(`/kiosk/operators`).then((r) => r.data);
export const kioskClock = (operatorId, action, photoBlob) => {
  const fd = new FormData();
  fd.append("operator_id", operatorId);
  fd.append("action", action);
  fd.append("photo", photoBlob, "clock.jpg");
  return http.post(`/kiosk/clock`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// ----- Staff: Complaints -----
export const getComplaints = (params = {}) => http.get(`/complaints`, { params }).then((r) => r.data);
export const createComplaint = (p) => http.post(`/complaints`, p).then((r) => r.data);
export const updateComplaint = (id, p) => http.put(`/complaints/${id}`, p).then((r) => r.data);
export const deleteComplaint = (id) => http.delete(`/complaints/${id}`).then((r) => r.data);

// ----- Staff: Tasks -----
export const getTasks = (params = {}) => http.get(`/tasks`, { params }).then((r) => r.data);
export const createTask = (p) => http.post(`/tasks`, p).then((r) => r.data);
export const updateTask = (id, p) => http.put(`/tasks/${id}`, p).then((r) => r.data);
export const setTaskStatus = (id, status) => http.put(`/tasks/${id}/status`, { status }).then((r) => r.data);
export const deleteTask = (id) => http.delete(`/tasks/${id}`).then((r) => r.data);

// Task deep-link sections -> in-app routes (used by Tasks page)
export const TASK_LINK_SECTIONS = [
  { value: "haccp", path: "/haccp" },
  { value: "productions", path: "/productions" },
  { value: "milk", path: "/milk" },
  { value: "planning", path: "/planning" },
  { value: "stock", path: "/stock" },
  { value: "templates", path: "/templates" },
  { value: "orders", path: "/orders" },
];

// ----- HACCP Traceability -----
export const getTraceability = (code) =>
  http.get(`/traceability`, { params: { code } }).then((r) => r.data);

// Format seconds as "Hh Mm"
export const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(Number(secs) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0 && m === 0) return "0m";
  return `${h > 0 ? `${h}h ` : ""}${m}m`;
};
